// Small, allowlisted product events. Drawings, mood, saves and coordinates never enter this API.
export const ANALYTICS_CONSENT_KEY='allwalks-analytics-consent-v1';
const CHAPTERS=new Set(['paths','relationships','emotions','discomfort','resilience','walk']);
const SCHEMA={
 world_ready:{renderer:['webgl','canvas']},world_error:{reason:['startup','runtime']},
 relationship_choice:{chapter:CHAPTERS,role:['lead','follow']},
 journey_start:{method:['designed','skipped','returning']},
 chapter_view:{chapter:CHAPTERS},chapter_engagement:{chapter:CHAPTERS,active_seconds:'number'},
 first_move:{chapter:CHAPTERS,input_method:['keyboard','pointer']},
 designer_open:{entry:['initial','redesign']},designer_complete:{entry:['initial','redesign'],design_used:['yes','no']},
 designer_cancel:{entry:['initial','redesign']},
 designer_skip:{entry:['initial','redesign']},
 designer_interaction:{entry:['initial','redesign'],action:['paint','colour','turn']},
 journey_start_click:{method:['designed','skipped']},
 chapter_enter:{chapter:CHAPTERS},
 chapter_navigate:{chapter:CHAPTERS,target_chapter:CHAPTERS,navigation_method:['chapter_list','next_button','scroll','swipe','keyboard','home','summary','hash']},
 attention_engagement:{chapter:CHAPTERS,surface:['world','designer','chapter_description','about','island'],active_seconds:'number'},
 control_use:{chapter:CHAPTERS,control:['menu','about','sound_on','sound_off','restart','balance','balance_reset','join','separate']},
 save_status:{state:['failed','recovered']},
 site_engagement:{visible_seconds:'number'},
 description_open:{description:['about','chapter','island'],chapter:CHAPTERS,source:['footer','menu','story_card','sign']},
 description_engagement:{description:['about','chapter','island'],chapter:CHAPTERS,visible_seconds:'number'},
 island_sign_tap:{chapter:CHAPTERS,destination:['island']}
};
export function createAnalytics({measurementId='',local=false,debug=false,win=window,doc=document,now=()=>performance.now(),onChange=()=>{}}={}){
 const configured=/^G-[A-Z0-9]+$/.test(measurementId)&&!local;
 let allowed=false,initialized=false,chapter=null,active=false,segmentStart=0,ready=null,journey=null,designer=null;
 let visible=false,visibleStart=0,description=null,descriptionStart=0;
 // Separate from the legacy visible timers: never infer attention from an open page.
 let focused=false,attentionCursor=now(),attentionMs=0,lastInteraction=-Infinity,enteredChapter=null;
 const designerActions=new Set(),IDLE_MS=30000;
 let loadState='idle',loadAttempts=0,loadEpoch=0,loadTimer=null,script=null;
 const MAX_LOAD_ATTEMPTS=3,LOAD_TIMEOUT_MS=15000;
 const moved=new Set();
 const notify=()=>{try{onChange();}catch{/* Status updates must never break the world. */}};
 const command=(...args)=>{try{win.gtag(...args);}catch{/* Analytics must never break the world. */}};
 function clearLoad(remove){
  if(loadTimer!==null){try{win.clearTimeout?.(loadTimer);}catch{}loadTimer=null;}
  const current=script;script=null;
  if(current){current.onload=null;current.onerror=null;if(remove)try{current.remove?.();}catch{}}
 }
 function loadTag(){
  if(!allowed||!configured||loadState==='loading'||loadState==='ready')return false;
  if(loadAttempts>=MAX_LOAD_ATTEMPTS){loadState='error';notify();return false;}
  loadAttempts++;const epoch=++loadEpoch;loadState='loading';notify();
  const settle=state=>{
   if(epoch!==loadEpoch||!allowed||loadState!=='loading')return;
   loadState=state;clearLoad(state==='error');notify();
  };
  try{
   const current=doc.createElement('script');script=current;
   current.async=true;current.src='https://www.googletagmanager.com/gtag/js?id='+measurementId;
   current.onload=()=>settle('ready');current.onerror=()=>settle('error');
   if(typeof win.setTimeout==='function')loadTimer=win.setTimeout(()=>settle('error'),LOAD_TIMEOUT_MS);
   doc.head.appendChild(current);
  }catch{settle('error');}
  return true;
 }
 function retry(){return allowed&&loadState==='error'&&loadAttempts<MAX_LOAD_ATTEMPTS?loadTag():false;}
 function track(name,params={}){
  if(!allowed||!configured||!SCHEMA[name])return false;
  const clean={};for(const [key,rule] of Object.entries(SCHEMA[name])){
   const value=params[key];if(rule==='number'){if(Number.isFinite(value))clean[key]=Math.max(0,Math.min(86400,Math.round(value*1000)/1000));}
   else if(rule instanceof Set?rule.has(value):rule.includes(value))clean[key]=value;
  }
  if(Object.keys(clean).length!==Object.keys(SCHEMA[name]).length)return false;
  command('event',name,{...clean,...(['site_engagement','description_engagement','chapter_engagement','attention_engagement'].includes(name)?{transport_type:'beacon'}:{}),...(debug?{debug_mode:true}:{})});return true;
 }
 function attentionSurface(){return description?(description.description==='chapter'?'chapter_description':description.description):designer?'designer':active&&journey?'world':null;}
 function accrueAttention(){
  const current=now();
  if(allowed&&visible&&focused&&chapter&&attentionSurface())attentionMs+=Math.max(0,Math.min(current,lastInteraction+IDLE_MS)-attentionCursor);
  attentionCursor=current;
 }
 function flushAttention(force=false){
  accrueAttention();
  if(attentionMs>=1000||force){
   if(attentionMs>0&&chapter&&attentionSurface())track('attention_engagement',{chapter,surface:attentionSurface(),active_seconds:attentionMs/1000});
   attentionMs=0;
  }
 }
 function interaction(){if(!allowed||!visible||!focused)return;accrueAttention();lastInteraction=now();}
 function focus(value){flushAttention(true);focused=Boolean(value);lastInteraction=-Infinity;}
 function flush(forceAttention=false){
  flushAttention(forceAttention);
  const current=now();
  if(active){const seconds=(current-segmentStart)/1000;if(seconds>=1||!allowed){segmentStart=current;if(seconds>=1&&chapter)track('chapter_engagement',{chapter,active_seconds:seconds});}}
  if(visible){const seconds=(current-visibleStart)/1000;if(seconds>=1||!allowed){visibleStart=current;if(seconds>=1)track('site_engagement',{visible_seconds:seconds});}
   if(description){const seconds=(current-descriptionStart)/1000;if(seconds>=1||!allowed){descriptionStart=current;if(seconds>=1)track('description_engagement',{...description,visible_seconds:seconds});}}
  }
 }
 function foreground(value){flush(true);visible=Boolean(value);visibleStart=descriptionStart=now();lastInteraction=-Infinity;}
 function descriptionOpen(id,source){flush(true);description=allowed?{description:id,chapter}:null;descriptionStart=now();if(description)track('description_open',{...description,source});}
 function descriptionClose(){flush(true);description=null;}

 function setConsent(value,persist){
  flush(true);const wasAllowed=allowed;allowed=value===true&&configured;lastInteraction=-Infinity;
  if(persist)try{win.localStorage.setItem(ANALYTICS_CONSENT_KEY,value?'granted':'denied');}catch{}
  if(!configured){notify();return;}
  win['ga-disable-'+measurementId]=!allowed;
  if(!allowed){
   if(initialized)command('consent','update',{analytics_storage:'denied'});
   if(loadState==='loading'){loadEpoch++;clearLoad(true);loadState='idle';}
   notify();return;
  }
  if(!initialized){
   win.dataLayer=win.dataLayer||[];win.gtag=function(){win.dataLayer.push(arguments);};
   command('consent','default',{analytics_storage:'denied',ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied'});
   command('consent','update',{analytics_storage:'granted'});command('js',new Date());
   // A fixed title and query-free location avoid accidental personal data in URLs.
   command('config',measurementId,{send_page_view:false,allow_google_signals:false,allow_ad_personalization_signals:false,
    page_location:win.location.origin+win.location.pathname,page_title:'All Walks',page_referrer:''});
   initialized=true;
   // Queue this once per page. A loader retry keeps this queue without adding a second page view.
   command('event','page_view',{page_title:'All Walks',page_location:win.location.origin+win.location.pathname,page_referrer:'',...(debug?{debug_mode:true}:{})});
  }else if(!wasAllowed)command('consent','update',{analytics_storage:'granted'});
  if(!wasAllowed){
   segmentStart=visibleStart=descriptionStart=now();moved.clear();
   if(ready)track('world_ready',{renderer:ready});
   if(journey)track('journey_start',{method:journey});
   if(designer)track('designer_open',{entry:designer});
   if(chapter)track('chapter_view',{chapter});
   loadTag();
  }
  notify();
 }
 function consent(value){setConsent(value,true);}
 function enterChapter(){if(active&&ready&&journey&&chapter&&enteredChapter!==chapter){if(track('chapter_enter',{chapter}))enteredChapter=chapter;}}
 function setChapter(next){if(!CHAPTERS.has(next)||next===chapter)return;flush(true);chapter=next;segmentStart=now();track('chapter_view',{chapter});enterChapter();}
 function activity(value){flush(true);active=Boolean(value);segmentStart=now();enterChapter();}
 function movement(input_method){if(!chapter||moved.has(chapter))return;if(track('first_move',{chapter,input_method}))moved.add(chapter);}
 function worldReady(renderer){ready=renderer;track('world_ready',{renderer});enterChapter();}
 function startJourney(method){flush(true);journey=method;track('journey_start',{method});enterChapter();}
 function designerOpen(entry){flush(true);designer=entry;designerActions.clear();track('designer_open',{entry});}
 function designerInteraction(action){if(!designer||designerActions.has(action))return;if(track('designer_interaction',{entry:designer,action}))designerActions.add(action);}
 function designerClose(committed,designUsed,skipped=false){flush(true);if(designer)track(skipped?'designer_skip':committed?'designer_complete':'designer_cancel',committed&&!skipped?{entry:designer,design_used:designUsed?'yes':'no'}:{entry:designer});designer=null;}
 let stored=null;try{stored=win.localStorage.getItem(ANALYTICS_CONSENT_KEY);}catch{}
 // The owner-selected default is automatic collection. Only an explicit visitor choice
 // is persisted; a previously saved refusal continues to take precedence.
 setConsent(stored!=='denied',false);
 return {track,consent,retry,setChapter,activity,foreground,focus,interaction,descriptionOpen,descriptionClose,flush,movement,worldReady,startJourney,designerOpen,designerInteraction,designerClose,
  get configured(){return configured;},get allowed(){return allowed;},get loadState(){return loadState;},
  get canRetry(){return allowed&&loadState==='error'&&loadAttempts<MAX_LOAD_ATTEMPTS;}};
}
export function installAnalytics(){
 const meta=document.querySelector('meta[name="ga4-measurement-id"]');
 const preview=!['http:','https:'].includes(location.protocol)||!location.hostname;
 const local=preview||(['localhost','127.0.0.1','[::1]'].includes(location.hostname)&&meta?.dataset.allowLocal!=='true');
 // Analytics starts without a prompt. Retry transient loader failures automatically,
 // within createAnalytics' existing three-attempt limit.
 let analytics,retryTimer=null;
 const update=()=>{
  if(!analytics?.canRetry||retryTimer!==null)return;
  retryTimer=window.setTimeout(()=>{retryTimer=null;analytics.retry();},1000);
 };
 analytics=createAnalytics({measurementId:meta?.content,local,debug:meta?.dataset.debug==='true',onChange:update});
 update();analytics.foreground(!document.hidden);
 const hasFocus=()=>document.hasFocus?.()===true&&!document.hidden;
 analytics.focus(hasFocus());
 window.addEventListener?.('focus',()=>analytics.focus(hasFocus()));
 window.addEventListener?.('blur',()=>analytics.focus(false));
 document.addEventListener?.('visibilitychange',()=>analytics.focus(hasFocus()));
 const input=e=>{if(e.isTrusted&&hasFocus())analytics.interaction();};
 for(const type of ['pointerdown','keydown','wheel','touchstart'])document.addEventListener?.(type,input,{capture:true,passive:true});
 document.addEventListener?.('pointermove',e=>{if(e.buttons)input(e);},{capture:true,passive:true});
 // Flush bounded time chunks so a long continuous visit is represented before exit.
 if(analytics.configured)setInterval(()=>analytics.flush(),15000);
 return analytics;
}

